import _ from "lodash";
import moment from "moment";
import React, { memo } from "react";

interface Round {
  review_time_days?: number | null;
}

interface Task {
  due_at?: string | null;
  approval_date?: string | null;
  submission_round_count?: number;
}

interface Props {
  task: Task;
  rounds: Round[];
}

interface MetricCellProps {
  label: string;
  value: React.ReactNode;
}

const MetricCell = ({ label, value }: MetricCellProps) => (
  <div className="flex flex-col items-center flex-1 p-4 bg-white rounded shadow-sm min-w-0">
    <div className="text-12 font-bold uppercase tracking-wide text-black-60 mb-1 text-center">
      {label}
    </div>
    <div className="text-24 font-bold text-black-80 leading-none">{value}</div>
  </div>
);

const SubmissionRoundsSummaryStrip = ({ task, rounds }: Props) => {
  const totalRounds = task.submission_round_count ?? rounds.length;

  const dueAt = task.due_at ? moment(task.due_at) : null;
  const approvalDate = task.approval_date ? moment(task.approval_date) : null;
  const referenceDate = approvalDate || moment();
  const daysOverdue = dueAt
    ? Math.max(0, referenceDate.diff(dueAt, "days"))
    : null;
  const daysOverdueDisplay =
    daysOverdue === null
      ? "—"
      : daysOverdue === 0
      ? "On time"
      : `+${daysOverdue}`;

  const daysInReview = _.sumBy(rounds, (r: Round) =>
    typeof r.review_time_days === "number" ? r.review_time_days : 0
  );

  const originalDueDate = dueAt ? dueAt.format("D MMM YYYY") : "—";

  const approvalDateDisplay = approvalDate
    ? approvalDate.format("D MMM YYYY")
    : "Pending";

  return (
    <div className="flex gap-3 mb-6">
      <MetricCell label="Rounds" value={totalRounds} />
      <MetricCell
        label="Days overdue"
        value={
          <span
            className={
              daysOverdue != null && daysOverdue > 0
                ? "text-error-darker"
                : "text-success-darker"
            }
          >
            {daysOverdueDisplay}
          </span>
        }
      />
      <MetricCell label="Days in review" value={daysInReview} />
      <MetricCell label="Original due date" value={originalDueDate} />
      <MetricCell label="Approval date" value={approvalDateDisplay} />
    </div>
  );
};

export default memo(SubmissionRoundsSummaryStrip);
